import { useClerk } from "@clerk/react";
import { Link } from "wouter";
import { useMe } from "@/lib/auth";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, LogOut, UserCog } from "lucide-react";
import { METALUI_BRAND } from "@workspace/brand";

export default function PendingApprovalScreen() {
  const { me } = useMe();
  const { signOut } = useClerk();
  const { resolvedTheme } = useTheme();
  // `bg-background` adapts to the theme → pick the logo variant that reads:
  // white on the dark theme, black on the light theme.
  const logoSrc = `${import.meta.env.BASE_URL}${
    resolvedTheme === "dark" ? METALUI_BRAND.logoFileWhite : METALUI_BRAND.logoFile
  }`;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-8 bg-background p-6">
      <img src={logoSrc} alt={METALUI_BRAND.appName} className="h-10 w-auto" />
      <Card className="max-w-md w-full">
        <CardHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            <CardTitle>Awaiting access approval</CardTitle>
          </div>
          <CardDescription>
            Your account <span className="font-medium text-foreground">{me?.email}</span>{" "}
            has been created, but it isn't authorised to use this workspace yet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p>
            Access is restricted. An administrator must review and approve your account before you can sign in.
          </p>
          <p className="text-muted-foreground">
            Please contact an administrator and ask them to grant your account a role.
          </p>
          <Button asChild variant="outline" className="w-full" data-testid="button-pending-account">
            <Link href="/account">
              <UserCog className="h-4 w-4 mr-2" /> Manage account &amp; password
            </Link>
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => signOut()}
            data-testid="button-pending-signout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
